import { globalOnDemandVars } from '../helper/context'
import { isDueDate } from './functions'
import { getScoreByRating } from './constants'
import { getMemorizingStatus } from './notebook'
import { updateRow } from '../backend/spreadsheet/updateRow'
import { getNewRecord, getRatingAsNumber } from './SM2+'
import { produce } from 'immer'
import { defer } from 'lodash'

export function nodeToSpreadsheetRootNode (node) {
  return {
    ...node,
    children: [],
    isSpreadsheetRootNode: true,
    isMultiple: globalOnDemandVars.spreadsheets()[node.spreadsheetId].switchForMultipleCard
  }
}

export function rowToNode (row, spreadsheetId, nodeId) {
  const configForRow = row.configForRow
  return {
    rowProperties: {
      ...row
    },
    id: configForRow.id,
    spreadsheetId: spreadsheetId,
    editableValues: {
      ...configForRow,
      parentId: nodeId
    },
    isSpreadsheetNode: true
  }
}

export function getReadyForSpreadsheet (spreadsheetRootNode, dueDateSet, reStudySet, reTestSet, totalScoreObject, path) {
  const spreadsheetId = spreadsheetRootNode.spreadsheetId
  const spreadsheet = globalOnDemandVars.spreadsheets()[spreadsheetId]
  let rows = spreadsheet.rows
  rows = rows.filter((row) => {
    const frontArray = spreadsheet.selectedItems1
    const backArray = spreadsheet.selectedItems2

    return frontArray.some((key) => !!row[key]) && backArray.some((key) => !!row[key])
  })
  let nodes = rows.map((row) => (rowToNode(row, spreadsheetId, spreadsheetRootNode.id)))

  const dailyLimitOn = globalOnDemandVars.dailyLimitOn()
  const dailyLimit = globalOnDemandVars.dailyLimit()
  const thoroughIds = globalOnDemandVars.thoroughIds().concat(globalOnDemandVars.addedThoroughIds())

  // const isThoroughNode = thoroughIds.some((oneId) => ((oneId === 'rootNode') || oneId === spreadsheetRootNode.id || (path.some((pathId) => (oneId === pathId)))))
  const isThoroughNode = thoroughIds.some((oneId) => {
    const isThisNodeThorough = ((oneId === 'rootNode') || oneId === spreadsheetRootNode.id || (path.some((pathId) => (oneId === pathId))))

    defer(() => {
      if (isThisNodeThorough) {
        if (oneId === 'rootNode') {
          globalOnDemandVars.set_courseStatus(produce(globalOnDemandVars.courseStatus(), draft => {
            draft.thoroughNodes = [globalOnDemandVars.topRootName()]
          }))
        } else if (oneId === spreadsheetRootNode.id) {
          globalOnDemandVars.set_courseStatus(produce(globalOnDemandVars.courseStatus(), draft => {
            if (Array.isArray(draft.thoroughNodes)) {
              draft.thoroughNodes.push(spreadsheetRootNode.title)
            } else {
              draft.thoroughNodes = [ spreadsheetRootNode.title ]
            }
          }))
        }
      }
    })

    return isThisNodeThorough
  })

  for (const node of nodes) {
    if (dailyLimitOn && (dailyLimit <= totalScoreObject.nodeCountAtFirst)) {
      break
    }
    if (node.editableValues.failStatus && node.editableValues.failStatus !== 'solved') {
      switch (node.editableValues.failStatus) {
        case 'reStudy':
          reStudySet.add(node.id)
          node.editableValues.memorizingStatus = 'reStudy'
          totalScoreObject.score += getScoreByRating('reStudy')
          totalScoreObject.score += getScoreByRating('reTest')
          totalScoreObject.nodeCountAtFirst += 1
          break
        case 'reTest':
          reTestSet.add(node.id)
          node.editableValues.memorizingStatus = 'reTest'
          totalScoreObject.score += getScoreByRating('reTest')
          totalScoreObject.nodeCountAtFirst += 1
          break
      }
    } else if (isDueDate(node) || isThoroughNode) {
      dueDateSet.add(node.id)
      const memorizingStatus = getMemorizingStatus(node)
      node.editableValues.memorizingStatus = memorizingStatus
      totalScoreObject.score += getScoreByRating(memorizingStatus)
      totalScoreObject.nodeCountAtFirst += 1
      if (memorizingStatus === 'firstTime') {
        node.editableValues.firstTime = true
        totalScoreObject.firstTimeNodeCount += 1
      }
    }
  }
  Object.defineProperty(spreadsheetRootNode, 'children', {
    value: nodes,
    configurable: false,
    writable: false,
    enumerable: true
  })
  Object.defineProperty(spreadsheetRootNode, 'getReady', {
    get () {
      return this === spreadsheetRootNode
    }
  })
}

export function updateRowValues (node, newRowProperties) {
  const nodeId = node.id
  const rows = globalOnDemandVars.spreadsheets()[node.spreadsheetId].rows
  const rowIndex = rows.findIndex((row) => (row.configForRow.id === node.id))
  const row = rows[rowIndex]

  const nextRowProperties = {
    ...row,
    ...newRowProperties
  }

  node.editableValues.newRowProperties = nextRowProperties

  rows[rowIndex] = nextRowProperties

  updateRow(node.spreadsheetId, nodeId, nextRowProperties)
}

export function prettifierForSpreadsheet (root, expectedRating) {
  const doseFilterNeed = !(root.cutAlready)
  return {
    rootNode: root,
    chunk: !doseFilterNeed ? root.children : root.children.filter((node) => node.editableValues && node.editableValues.memorizingStatus === expectedRating)
  }
}

// -----------------------------------------------------------------------------------------------------------

function getNewRecordOfNode (node, rating) {
  return getNewRecord({
    difficulty: node.editableValues.difficulty,
    interval: node.editableValues.interval,
    update: node.editableValues.update
  }, getRatingAsNumber(rating))
}

export function updateCardBySM2 (node, rating, updateIdentifier) {
  const rows = globalOnDemandVars.spreadsheets()[node.spreadsheetId].rows
  const rowIndex = rows.findIndex((row) => (row.configForRow.id === node.id))
  const row = rows[rowIndex]

  if (process.env.NODE_ENV !== 'production') {
    console.tron.display({
      name: 'logic / updateCardBySM2',
      value: {
        node,
        rows,
        rowIndex,
        row,
        rowExtensible: Object.isExtensible(row),
        rowFrozen: Object.isFrozen(row),
        rowSealed: Object.isSealed(row),
        rowCExtensible: Object.isExtensible(row.configForRow),
        rowCFrozen: Object.isFrozen(row.configForRow),
        rowCSealed: Object.isSealed(row.configForRow)
      }
      // , important: true
    })
  }

  let newRecord
  if (!node.editableValues.updateIdentifier || node.editableValues.updateIdentifier !== updateIdentifier) {
    node.editableValues.updateIdentifier = updateIdentifier

    node.editableValues.prevRating = rating

    node.editableValues.prevRecord = {
      difficulty: row.configForRow.difficulty,
      interval: row.configForRow.interval,
      update: row.configForRow.update
    }

    newRecord = getNewRecordOfNode({
      editableValues: {
        difficulty: row.configForRow.difficulty,
        interval: row.configForRow.interval,
        update: row.configForRow.update
      }
    }, rating)
    if (updateIdentifier.endsWith('reTest') && rating === 'hard') {
      newRecord.difficulty = row.configForRow.difficulty
    }

    node.editableValues.difficulty = newRecord.difficulty
    node.editableValues.interval = newRecord.interval
    node.editableValues.update = newRecord.update

    //  1) globalData update

    // TODO .configForRow 가 frozen 돼서 돌아가는 방법을 써야했음. 왜 어디서 frozen 되는거지??
    // row.configForRow.difficulty = newRecord.difficulty
    // row.configForRow.interval = newRecord.interval
    // row.configForRow.update = newRecord.update

    row.configForRow = {
      ...(row.configForRow),
      difficulty: newRecord.difficulty,
      interval: newRecord.interval,
      update: newRecord.update,
      ratingRecord: row.configForRow.ratingRecord.concat({
        rating: rating,
        time: Date.now()
      })
    }

    //  2) DB update
    updateRow(node.spreadsheetId, node.id, { configForRow: { ...row.configForRow } })
  } else {
    newRecord = getNewRecord(node.editableValues.prevRecord, getRatingAsNumber(rating))
    if (updateIdentifier.endsWith('reTest') && rating === 'hard') {
      newRecord.difficulty = row.configForRow.difficulty
    }

    node.editableValues.difficulty = newRecord.difficulty
    node.editableValues.interval = newRecord.interval
    node.editableValues.update = newRecord.update

    // row.configForRow.difficulty = newRecord.difficulty
    // row.configForRow.interval = newRecord.interval
    // row.configForRow.update = newRecord.update

    row.configForRow = {
      ...(row.configForRow),
      difficulty: newRecord.difficulty,
      interval: newRecord.interval,
      update: newRecord.update,
      ratingRecord: row.configForRow.ratingRecord.concat({
        rating: rating,
        time: Date.now()
      })
    }

    //  2) DB update
    updateRow(node.spreadsheetId, node.id, { configForRow: { ...row.configForRow } })
  }
}

export function downdateCardBySM2 (node) {
  const prevRecord = node.editableValues.prevRecord
  const nodeId = node.id

  const rows = globalOnDemandVars.spreadsheets()[node.spreadsheetId].rows
  const rowIndex = rows.findIndex((row) => (row.configForRow.id === node.id))
  const row = rows[rowIndex]

  row.configForRow.difficulty = prevRecord.difficulty
  row.configForRow.interval = prevRecord.interval
  row.configForRow.update = prevRecord.update
  row.configForRow.ratingRecord.pop()

  row.configForRow = {
    ...(row.configForRow),
    difficulty: prevRecord.difficulty,
    interval: prevRecord.interval,
    update: prevRecord.update,
    ratingRecord: row.configForRow.ratingRecord.slice(0, -1)
  }

  updateRow(node.spreadsheetId, nodeId, { configForRow: { ...row.configForRow } })
}

export function updateCardForFail (node, failStatus) {
  const nodeId = node.id
  const rows = globalOnDemandVars.spreadsheets()[node.spreadsheetId].rows
  const rowIndex = rows.findIndex((row) => (row.configForRow.id === node.id))
  const row = rows[rowIndex]
  // 1) globalData update
  // row.configForRow.failStatus = failStatus
  row.configForRow = {
    ...(row.configForRow),
    failStatus: failStatus
  }

  // 2) DB update

  updateRow(node.spreadsheetId, nodeId, { configForRow: { ...row.configForRow } })
}
